import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import Login from "./Login";
import "../App.css";
import "./Shell.css";
import api from "../api.js";
import useChatNotifications from "../hooks/useChatNotifications";
import { NavIcon, buildNav, TITLES, SUBTITLES } from "./navConfig";
import { homePath } from "./ProtectedRoute";
import { Logo } from '../ds';

// Инициалы для кружка профиля: из имени, иначе из логина.
function initials(name, login) {
  const src = (name || login || "").trim();
  if (!src) return "?";
  const parts = src.split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((p) => p[0]).join("").toUpperCase();
}

export default function Root() {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoginPage = /^\/login\/?$/.test(location.pathname);

  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partnerToSearch, setPartnerToSearch] = useState("");
  const [openExitModal, setOpenExitModal] = useState(false);
  const [railOpen, setRailOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("wave-theme") || "light");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("wave-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  useEffect(() => {
    if (isLoginPage) {
      setMe(null);
      setLoading(false);
      return;
    }

    const init = async () => {
      try {
        const res = await api.get("/api/auth/me");
        setMe(res.data);
      } catch {
        setMe(null);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [isLoginPage]);

  const handleLogin = async () => {
    try {
      const res = await api.get("/api/auth/me");
      setMe(res.data);
      navigate(homePath(res.data));
    } catch {
      setMe(null);
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      await api.post("/api/auth/logout");
    } catch (e) {
      console.error("Ошибка выхода", e);
    }
    setMe(null);
    navigate("/login");
    setOpenExitModal(false);
  };

  const handleSearchByCode = (e) => {
    e.preventDefault();
    if (!partnerToSearch.trim()) return;
    navigate(`/partners?code=${partnerToSearch.trim()}`);
    setPartnerToSearch("");
  };

  const isChatRoute = location.pathname.startsWith("/chat");
  const { notifOn, enable: enableNotifs, unread, clearUnread } = useChatNotifications({
    enabled: !!me && !isLoginPage,
    isChatRoute,
  });

  useEffect(() => {
    if (isChatRoute) clearUnread();
  }, [isChatRoute, clearUnread]);

  // Переход на другой раздел закрывает выдвижной рельс на телефоне.
  useEffect(() => { setRailOpen(false); }, [location.pathname]);

  if (loading) return null;

  if (!me || isLoginPage) return <Login onLogin={handleLogin} />;

  const role = me?.userInfo?.role;
  const partnerName = me?.userInfo?.partnerName;
  const groups = buildNav(me?.tabs);

  const routeKey = location.pathname.split("/")[1] || "me";
  const fromTabs = (me?.tabs || []).find((t) => t.key === routeKey)?.title;
  const title = fromTabs || TITLES[routeKey] || "";
  // Партнёр всегда видит, от чьего имени работает; админу показываем описание раздела.
  const context = role === "Партнер" ? partnerName : SUBTITLES[routeKey];

  return (
    <div className="shell">
      <div
        className={`rail-scrim ${railOpen ? "on" : ""}`}
        onClick={() => setRailOpen(false)}
      />

      <nav className={`rail ${railOpen ? "open" : ""}`}>
        <NavLink to="/me" className="rail-brand">
          <Logo variant="app" appTone="white" height={36} className="rail-mark" title="WAve" />
          <span className="rail-brand-text">
            <span className="rail-brand-name"><Logo variant="wordmark" height={17} /></span>
            <span className="rail-brand-sub">{partnerName || "Администрирование"}</span>
          </span>
        </NavLink>

        {role === "Админ" && (
          <form className="rail-search" onSubmit={handleSearchByCode}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              placeholder="Поиск партнёра"
              aria-label="Поиск партнёра по коду"
              value={partnerToSearch}
              onChange={(e) => setPartnerToSearch(e.target.value)}
            />
          </form>
        )}

        <div className="rail-nav">
          {groups.map((g) => (
            <div className="rail-group" key={g.label}>
              <div className="rail-group-label">{g.label}</div>
              {g.items.map((it) => (
                <NavLink key={it.key} to={`/${it.key}`} className="rail-link">
                  <span className={`rail-ico cat-${it.key}`}>
                    <NavIcon name={it.key} size={18} />
                  </span>
                  <span className="rail-link-label">{it.title}</span>
                  {it.key === "chat" && unread > 0 && (
                    <span className="rail-count">{unread > 99 ? "99+" : unread}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        <div className="rail-foot">
          <NavLink to="/me" className="rail-me">
            <span className="rail-me-pic">
              {initials(me?.userInfo?.name, me?.userInfo?.login)}
            </span>
            <span className="rail-me-text">
              <span className="rail-me-name">{me?.userInfo?.name || me?.userInfo?.login}</span>
              <span className="rail-me-role">{role}</span>
            </span>
          </NavLink>
          <button
            type="button"
            className="rail-icon-btn"
            onClick={toggleTheme}
            title={theme === "dark" ? "Светлая тема" : "Тёмная тема"}
            aria-label={theme === "dark" ? "Светлая тема" : "Тёмная тема"}
          >
            {theme === "dark" ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="4.2" />
                <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M18.7 5.3l-1.4 1.4M6.7 17.3l-1.4 1.4" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 13.5A8 8 0 1 1 10.5 4a6.5 6.5 0 0 0 9.5 9.5z" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="rail-icon-btn"
            onClick={() => setOpenExitModal(true)}
            title="Выйти"
            aria-label="Выйти"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 17l5-5-5-5M20 12H9M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5" />
            </svg>
          </button>
        </div>
      </nav>

      <main className="page">
        <header className="page-top">
          <button
            type="button"
            className="rail-icon-btn rail-toggle"
            onClick={() => setRailOpen((v) => !v)}
            aria-label="Меню"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>

          <div className="page-title">
            <h1>{title}</h1>
            {context && (
              <>
                <span className="sep">/</span>
                <span className="ctx">{context}</span>
              </>
            )}
          </div>

          <button
            type="button"
            className={`notif-bell ${notifOn ? "on" : ""}`}
            onClick={enableNotifs}
            title={notifOn ? "Уведомления о новых сообщениях включены" : "Включить уведомления и звук о новых сообщениях"}
          >
            {/* SVG, а не эмодзи: красится темой и не зависит от шрифта эмодзи в системе */}
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 8-3 8h18s-3-1-3-8M13.7 21a2 2 0 0 1-3.4 0" />
              {!notifOn && <path d="M3.5 3.5l17 17" />}
            </svg>
            {unread > 0 && <span className="notif-badge">{unread > 99 ? "99+" : unread}</span>}
          </button>
        </header>

        {/* Класс main-content сохранён намеренно: на него завязана тема таблиц в theme.css.
            Раскладку теперь задаёт page-body. */}
        <div className="page-body main-content">
          <Outlet context={{ me, setOpenExitModal }} />
        </div>
      </main>

      {openExitModal && (
        <div className="modalOverlay">
          <div className="modalExit">
            <div className="exitModalTop">
              <h3>Вы уверены, что хотите выйти?</h3>
              <button onClick={() => setOpenExitModal(false)}></button>
            </div>
            <div className="buttonsModalExit">
              <button className="exitButton" onClick={handleLogout}>
                Да, выйти
              </button>
              <button
                className="cancelExit"
                onClick={() => setOpenExitModal(false)}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
